import { useStoreConfigContextSafe } from '../providers';
import { getNestedValue } from '../utils/get-nested-value';
import { useEffect } from 'react';

interface UseStoreConfigControllerProps<T> {
  path: string;
  onChange: (value: T) => void;
  value: T;
  // Dependency injection - optional config data
  storeConfig?: any;
}

interface UseStoreConfigControllerReturn<T> {
  baseValue: T;
  isBaseDifferent: boolean;
  restoreBaseValue: () => void;
}

// Safe hook that works without provider (dependency injection)
function useStoreConfigController<T>({
  path,
  onChange,
  value,
  storeConfig,
}: UseStoreConfigControllerProps<T>): UseStoreConfigControllerReturn<T> {
  // Try to get config from injected prop first, then fall back to safe context
  const contextData = useStoreConfigContextSafe();
  const config = storeConfig || contextData?.config || null;

  const baseValue = getNestedValue(config, path);

  useEffect(() => {
    if (!value && baseValue) {
      onChange(baseValue);
    }
  }, [value, baseValue]);

  return { baseValue, isBaseDifferent: baseValue !== value, restoreBaseValue: () => onChange(baseValue) };
}

export default useStoreConfigController;
